/**
 * WhatsApp click-to-send templates — v1 (free, manual-but-assisted).
 *
 * Per Docs/IMPLEMENTATION_PLAN_AWS.md §24, v1 ships seven bilingual templates
 * (EN + AR). Each template is rendered into a `https://wa.me/<phone>?text=…`
 * URL that staff can tap to open WhatsApp with the message pre-drafted.
 *
 * The exact same template IDs and variable names are reused unchanged when
 * v1.1 swaps in the Twilio WhatsApp Business API — only the delivery
 * mechanism changes.
 */

export type WhatsAppLocale = 'en' | 'ar';

export const WHATSAPP_TEMPLATE_IDS = [
  'customer_job_assigned',
  'customer_on_the_way',
  'customer_job_started',
  'customer_job_completed',
  'customer_job_media',
  'customer_invoice',
  'worker_job_assigned',
  'worker_sla_breach',
  'worker_temp_credentials',
  'admin_worker_on_the_way',
] as const;
export type WhatsAppTemplateId = (typeof WHATSAPP_TEMPLATE_IDS)[number];

/**
 * Variables every template may reference. Templates ignore vars they don't use.
 * Times should be pre-formatted for the recipient locale (e.g. "10:30 AM" /
 * "٠٠:٣٠ ص") because we don't ship a full date library here.
 */
export interface WhatsAppVars {
  customerName?: string;
  workerName?: string;
  serviceType?: string;
  scheduledTime?: string;
  eta?: string;
  feedbackUrl?: string;
  loginUrl?: string;
  username?: string;
  tempPassword?: string;
  /** Free-form additional context (used in worker_sla_breach). */
  reason?: string;
  /** Pre-formatted list of media links + captions (one per line). */
  mediaList?: string;
  /** Invoice number e.g. "FIN-2026-0001". */
  invoiceNumber?: string;
  /** Invoice total formatted (e.g. "AED 1,250.00"). */
  invoiceTotal?: string;
  /** Presigned PDF download URL. */
  invoiceUrl?: string;
  /** Customer address single-line (for admin notifications). */
  customerAddress?: string;
  /** Short job identifier (last 8 chars of UUID is fine). */
  jobId?: string;
}

type Template = (v: WhatsAppVars) => string;

const T: Record<WhatsAppTemplateId, Record<WhatsAppLocale, Template>> = {
  customer_job_assigned: {
    en: (v) =>
      `Hello ${v.customerName ?? ''}, your Fix It Ninjaz ${v.serviceType ?? ''} job has been booked for ${v.scheduledTime ?? ''}. ` +
      `Your technician ${v.workerName ?? ''} will be in touch. — Fix It Ninjaz`,
    ar: (v) =>
      `مرحبا ${v.customerName ?? ''}, تم حجز خدمة ${v.serviceType ?? ''} الخاصة بك مع Fix It Ninjaz في ${v.scheduledTime ?? ''}. ` +
      `سيتواصل معك الفني ${v.workerName ?? ''}. — Fix It Ninjaz`,
  },
  customer_on_the_way: {
    en: (v) =>
      `Hello ${v.customerName ?? ''}, your Fix It Ninja ${v.workerName ?? ''} is on the way for the ${v.serviceType ?? ''} service. ` +
      `ETA: ${v.eta ?? v.scheduledTime ?? 'shortly'}. — Fix It Ninjaz`,
    ar: (v) =>
      `مرحبا ${v.customerName ?? ''}, الفني ${v.workerName ?? ''} في الطريق لخدمة ${v.serviceType ?? ''}. ` +
      `الوصول المتوقع: ${v.eta ?? v.scheduledTime ?? 'قريباً'}. — Fix It Ninjaz`,
  },
  customer_job_started: {
    en: (v) =>
      `Hello ${v.customerName ?? ''}, ${v.workerName ?? 'your technician'} has started the ${v.serviceType ?? ''} work. ` +
      `We'll let you know when it's complete. — Fix It Ninjaz`,
    ar: (v) =>
      `مرحبا ${v.customerName ?? ''}, بدأ ${v.workerName ?? 'الفني'} العمل على خدمة ${v.serviceType ?? ''}. ` +
      `سنبلغك عند الانتهاء. — Fix It Ninjaz`,
  },
  customer_job_completed: {
    en: (v) =>
      `Hello ${v.customerName ?? ''}, the ${v.serviceType ?? ''} job is complete. Thank you for choosing Fix It Ninjaz! ` +
      `We'd love your feedback: ${v.feedbackUrl ?? 'https://fixitninjaz.com'}`,
    ar: (v) =>
      `مرحبا ${v.customerName ?? ''}, تم الانتهاء من خدمة ${v.serviceType ?? ''}. شكراً لاختيارك Fix It Ninjaz! ` +
      `يسعدنا سماع رأيك: ${v.feedbackUrl ?? 'https://fixitninjaz.com'}`,
  },
  customer_job_media: {
    en: (v) =>
      `Hello ${v.customerName ?? ''}, here are the photos / videos from your ${v.serviceType ?? ''} job:\n\n` +
      `${v.mediaList ?? ''}\n\n` +
      `Links expire in 1 hour. — Fix It Ninjaz`,
    ar: (v) =>
      `مرحبا ${v.customerName ?? ''}, هذه هي الصور / الفيديوهات الخاصة بخدمة ${v.serviceType ?? ''}:\n\n` +
      `${v.mediaList ?? ''}\n\n` +
      `تنتهي صلاحية الروابط خلال ساعة. — Fix It Ninjaz`,
  },
  customer_invoice: {
    en: (v) =>
      `Hello ${v.customerName ?? ''}, your invoice ${v.invoiceNumber ?? ''} for the ${v.serviceType ?? ''} job is ready. ` +
      `Total: ${v.invoiceTotal ?? ''}.\n\n` +
      `Download (link valid 1 hour): ${v.invoiceUrl ?? ''}\n\n` +
      `Thank you for choosing Fix It Ninjaz.`,
    ar: (v) =>
      `مرحبا ${v.customerName ?? ''}, فاتورتك ${v.invoiceNumber ?? ''} لخدمة ${v.serviceType ?? ''} جاهزة. ` +
      `الإجمالي: ${v.invoiceTotal ?? ''}.\n\n` +
      `للتحميل (الرابط ساري لمدة ساعة): ${v.invoiceUrl ?? ''}\n\n` +
      `شكراً لاختيارك Fix It Ninjaz.`,
  },
  worker_job_assigned: {
    en: (v) =>
      `New job: ${v.serviceType ?? ''} for ${v.customerName ?? ''} at ${v.scheduledTime ?? ''}. ` +
      `Open the app for details. — Fix It Ninjaz`,
    ar: (v) =>
      `مهمة جديدة: ${v.serviceType ?? ''} للعميل ${v.customerName ?? ''} في ${v.scheduledTime ?? ''}. ` +
      `افتح التطبيق للتفاصيل. — Fix It Ninjaz`,
  },
  worker_sla_breach: {
    en: (v) =>
      `Reminder: the ${v.serviceType ?? ''} job for ${v.customerName ?? ''} (scheduled ${v.scheduledTime ?? ''}) is overdue. ` +
      `${v.reason ?? 'Please update its status in the app or contact dispatch.'}`,
    ar: (v) =>
      `تذكير: مهمة ${v.serviceType ?? ''} للعميل ${v.customerName ?? ''} (المجدولة ${v.scheduledTime ?? ''}) متأخرة. ` +
      `${v.reason ?? 'يرجى تحديث الحالة في التطبيق أو الاتصال بالإدارة.'}`,
  },
  worker_temp_credentials: {
    en: (v) =>
      `Your Fix It Ninjaz worker app login\n` +
      `URL: ${v.loginUrl ?? 'https://app.fixitninjaz.com'}\n` +
      `Username: ${v.username ?? ''}\n` +
      `Temporary password: ${v.tempPassword ?? ''}\n` +
      `Please set a new password the first time you sign in.`,
    ar: (v) =>
      `بيانات تسجيل الدخول إلى تطبيق Fix It Ninjaz\n` +
      `الرابط: ${v.loginUrl ?? 'https://app.fixitninjaz.com'}\n` +
      `اسم المستخدم: ${v.username ?? ''}\n` +
      `كلمة المرور المؤقتة: ${v.tempPassword ?? ''}\n` +
      `يرجى تعيين كلمة مرور جديدة عند أول تسجيل دخول.`,
  },
  admin_worker_on_the_way: {
    en: (v) =>
      `\uD83D\uDE90 *On the way* — ${v.workerName ?? 'Worker'} heading to ${v.customerName ?? 'customer'} ` +
      `for ${v.serviceType ?? 'service'}${v.customerAddress ? ` at ${v.customerAddress}` : ''}. ` +
      `ETA ${v.eta ?? 'shortly'}.${v.jobId ? ` Job #${v.jobId}.` : ''}`,
    ar: (v) =>
      `\uD83D\uDE90 *في الطريق* — ${v.workerName ?? 'الفني'} متجه إلى ${v.customerName ?? 'العميل'} ` +
      `لخدمة ${v.serviceType ?? ''}${v.customerAddress ? ` في ${v.customerAddress}` : ''}. ` +
      `الوصول المتوقع ${v.eta ?? 'قريباً'}.${v.jobId ? ` رقم المهمة #${v.jobId}.` : ''}`,
  },
};

/** Render a WhatsApp template into raw text (no URL encoding). */
export function renderWhatsAppMessage(
  templateId: WhatsAppTemplateId,
  locale: WhatsAppLocale,
  vars: WhatsAppVars,
): string {
  const tpl = T[templateId]?.[locale];
  if (!tpl) throw new Error(`Unknown WhatsApp template: ${templateId}/${locale}`);
  return tpl(vars).trim();
}

/**
 * Normalise a UAE phone for `wa.me`. Strips `+`, spaces, dashes.
 * Accepts `+971501234567`, `00971501234567`, `0501234567`.
 */
export function normaliseWaPhone(phone: string): string {
  let p = phone.replace(/[\s-]/g, '').replace(/^\+/, '');
  if (p.startsWith('00')) p = p.slice(2);
  if (p.startsWith('0') && p.length === 10) p = `971${p.slice(1)}`;
  return p;
}

/**
 * Build a `https://wa.me/<phone>?text=<encoded message>` link.
 * The frontend opens this with `window.open(url, '_blank', 'noopener')`.
 */
export function buildWhatsAppLink(
  phone: string,
  templateId: WhatsAppTemplateId,
  vars: WhatsAppVars,
  locale: WhatsAppLocale = 'en',
): string {
  const text = renderWhatsAppMessage(templateId, locale, vars);
  return `https://wa.me/${normaliseWaPhone(phone)}?text=${encodeURIComponent(text)}`;
}
